import React, {useContext} from 'react';

import Button from '@pb/kit-component-button';
import {LocaleContext} from '@pb/kit-context-locale';
import {INTEGER} from '@pb/kit-formatters';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {FIRST_PAGE, PREV_CAPTION} from './constants';
import styles from './pagination.component.module.styl';

import type {LocaleContextType} from '@pb/kit-context-locale';
import type {PaginationRangeType} from '../typedef';
import type {PaginationStripeProps} from '../pagination-stripe/pagination-stripe.component';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

const noop = () => undefined;

type PaginationButtonProps = {
    onClick: (page: number) => void,
    page: number,
    currentPage?: number,
    isDisabled?: boolean,
    size?: PaginationStripeProps['size'],
};

export const PaginationButton = (props: PaginationButtonProps) => {
    const {locale} = useContext<LocaleContextType>(LocaleContext);

    const {
        currentPage,
        isDisabled,
        size = 'normal',
        onClick = noop,
        page,
    } = props;

    const ariaLabel = currentPage === page ? `${page} page` : `Go to ${page} page`;
    const buttonPaginationClasses = size === 'small'
        ? {className: classNames('pagination__button', 'pagination__button_small')} : null;

    return (
        <li className={classes.pagination__button}>
            <Button
                aria-label={ariaLabel}
                current={page === currentPage}
                disabled={isDisabled}
                onClick={() => onClick(page)}
                size={size === 'small' ? 'small' : undefined}
                {...buttonPaginationClasses}
                {...(page === currentPage ? {'aria-current': 'page'} : {})}
                variant="Type.PAGINATION">
                {INTEGER.format(page, locale)}
            </Button>
        </li>
    );
};

type PaginationSeparatorProps = {
    size?: PaginationStripeProps['size'],
    shown?: boolean,
    disabled?: boolean,
};

export const PaginationSeparator = ({
    size = 'normal',
    shown = false,
    disabled,
}: PaginationSeparatorProps) => {
    const paginationSeparatorClasses = classNames('pagination__separator', {
        pagination__separator_small: size === 'small',
        disabled,
    });

    return (shown ? <li className={paginationSeparatorClasses} aria-hidden={true}/> : null);
};

type NavigationButtonProps = {
    caption: string,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void,
    className?: string,
    isDisabled?: boolean,
    size?: PaginationStripeProps['size'],
};

export const NavigationButton = (props: NavigationButtonProps) => {
    const {
        className,
        caption,
        isDisabled,
        size = 'normal',
        onClick = noop,
    } = props;

    const getAriaLabel = (label: string) => {
        const generateAriaLabel = (text: string) => 'Go to ' + text.toLowerCase() + ' page';

        return generateAriaLabel(label === PREV_CAPTION ? 'previous' : label);
    };

    const navigationButtonClasses = classNames('pagination__navigation-button', {
        'pagination__navigation-button_small': size === 'small',
    });

    return (
        <li className={className}>
            <Button
                aria-label={getAriaLabel(caption)}
                className={navigationButtonClasses}
                disabled={isDisabled}
                onClick={onClick}
                size={size === 'small' ? 'small' : undefined}
                variant="Type.PAGINATION">
                {caption}
            </Button>
        </li>
    );
};

type PaginationInternalButtonsProps = {
    currentPage: number,
    onClick: (page: number) => void,
    range: PaginationRangeType,
    totalPages: number,
    isDisabled?: boolean,
    isNeedToFit?: boolean,
    size?: PaginationStripeProps['size'],
};

export const PaginationInternalButtons = (props: PaginationInternalButtonsProps) => {
    const {
        currentPage,
        isDisabled,
        size = 'normal',
        isNeedToFit = false,
        onClick = noop,
        range,
        totalPages,
    } = props;

    const handleClick = (page: number) => {
        onClick(page);
    };

    const prevPage = currentPage - 1;
    const nextPage = currentPage + 1;
    const arrPages = [];

    if (totalPages - 2 > range) {
        if (prevPage > FIRST_PAGE && nextPage < totalPages) {
            arrPages.push(...(!isNeedToFit ? [prevPage, currentPage, nextPage] : [prevPage, currentPage]));
        }
        if (prevPage <= FIRST_PAGE) {
            for (let i = FIRST_PAGE + 1; i <= FIRST_PAGE + range; i++) {
                arrPages.push(i);
            }
        }
        if (nextPage >= totalPages) {
            for (let i = totalPages - range; i < totalPages; i++) {
                arrPages.push(i);
            }
        }
    } else {
        for (let i = FIRST_PAGE + 1; i < totalPages; i++) {
            arrPages.push(i);
        }
    }

    return (
        <>
            {arrPages.map((page, index) => (
                <PaginationButton
                    key={index}
                    currentPage={currentPage}
                    isDisabled={isDisabled}
                    size={size}
                    onClick={handleClick}
                    page={page}
                />
            ))}
        </>
    );
};
